import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Legend, 
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis
} from 'recharts'
import type { ReportFilters } from '@/lib/reports-engine'
import { useClientReports } from '@/hooks/useReportsEngine'
import { Users, AlertTriangle, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

interface ClientReportTabProps {
  filters: ReportFilters
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })

const COLORS = [
  'hsl(224, 71%, 40%)',   // Primary blue
  'hsl(142, 71%, 45%)',   // Success green
  'hsl(38, 92%, 50%)',    // Warning orange
  'hsl(280, 65%, 55%)',   // Purple
  'hsl(190, 70%, 45%)',   // Teal
  'hsl(350, 65%, 55%)',   // Rose
  'hsl(45, 85%, 50%)',    // Yellow
  'hsl(210, 50%, 60%)',   // Light blue
]

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium mb-2">{data.name}</p>
        <div className="space-y-1 text-muted-foreground">
          <p>Receita: <span className="text-foreground font-medium">{formatCurrency(data.totalRevenue)}</span></p>
          <p>Participação: <span className="text-foreground font-medium">{data.revenueShare}%</span></p>
          <p>Projetos: <span className="text-foreground font-medium">{data.projectCount}</span></p>
          <p>Taxa efetiva: <span className="text-foreground font-medium">{formatCurrency(data.effectiveRate)}/h</span></p>
        </div>
      </div>
    )
  }
  return null
}

export function ClientReportTab({ filters }: ClientReportTabProps) {
  const { data: clients, isLoading } = useClientReports(filters)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-[300px] rounded-xl" />
          <Skeleton className="h-[300px] rounded-xl" />
        </div>
      </div>
    )
  }

  const clientsWithRevenue = (clients || []).filter(c => c.totalRevenue > 0)
  const highDependencyClients = clientsWithRevenue.filter(c => c.isHighDependency)
  const topClient = clientsWithRevenue[0]

  // Prepare pie data
  const pieData = clientsWithRevenue.slice(0, 8).map((client, index) => ({
    ...client,
    fill: COLORS[index % COLORS.length]
  }))

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {highDependencyClients.length > 0 && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-warning">Risco de Dependência</p>
            <p className="text-sm text-muted-foreground mt-1">
              {highDependencyClients.map(c => c.name).join(', ')} representa(m) mais de 50% da receita.
              Considere diversificar sua base de clientes.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Donut chart */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Receita por Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="totalRevenue"
                      nameKey="name"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      formatter={(value) => (
                        <span className="text-xs text-foreground">{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <p className="text-muted-foreground">Sem dados de clientes no período</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top client card */}
        {topClient && (
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Crown className="h-4 w-4 text-warning" />
                Top Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-warning/10 to-warning/5 border border-warning/20 rounded-xl">
                <p className="text-2xl font-bold">{topClient.name}</p>
                <p className="text-3xl font-display font-bold text-warning mt-2">
                  {formatCurrency(topClient.totalRevenue)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {topClient.revenueShare}% da receita total
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/50 rounded-xl">
                  <p className="text-xs text-muted-foreground">Projetos</p>
                  <p className="text-xl font-bold">{topClient.projectCount}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-xl">
                  <p className="text-xs text-muted-foreground">Taxa Efetiva</p>
                  <p className="text-xl font-bold">{formatCurrency(topClient.effectiveRate)}/h</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-xl">
                  <p className="text-xs text-muted-foreground">Horas</p>
                  <p className="text-xl font-bold">{topClient.totalHours}h</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-xl">
                  <p className="text-xs text-muted-foreground">Média/Projeto</p>
                  <p className="text-xl font-bold">{formatCurrency(topClient.avgProjectValue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Client ranking table */}
      {clientsWithRevenue.length > 0 && (
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ranking de Clientes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">#</th>
                    <th className="pb-2 font-medium text-muted-foreground">Cliente</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Receita</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">%</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Projetos</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Horas</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Taxa/h</th>
                  </tr>
                </thead>
                <tbody>
                  {clientsWithRevenue.map((client, index) => (
                    <tr key={client.id} className="border-b border-dashed last:border-0">
                      <td className="py-2 text-muted-foreground">{index + 1}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{client.name}</span>
                          {client.isHighDependency && (
                            <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/20">
                              Alta dependência
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-right font-medium">{formatCurrency(client.totalRevenue)}</td>
                      <td className={cn(
                        "py-2 text-right",
                        client.revenueShare > 50 ? "text-warning font-medium" : ""
                      )}>
                        {client.revenueShare}%
                      </td>
                      <td className="py-2 text-right">{client.projectCount}</td>
                      <td className="py-2 text-right">{client.totalHours}h</td>
                      <td className="py-2 text-right">{formatCurrency(client.effectiveRate)}/h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
